/**
 * Quick-fill chips for prediction entry.
 * Common scorelines + recent personal favorites (per pool, in localStorage).
 */

import { useEffect, useState, useCallback } from 'react';
import { cn } from '../../lib/utils';

export interface QuickFillScore {
  home: number;
  away: number;
  label: string;
}

const COMMON_SCORES: QuickFillScore[] = [
  { home: 1, away: 0, label: '1-0' },
  { home: 2, away: 1, label: '2-1' },
  { home: 1, away: 1, label: 'Empate (1-1)' },
  { home: 2, away: 0, label: '2-0' },
  { home: 0, away: 0, label: '0-0' },
];

const RECENTS_STORAGE_KEY = (poolSlug: string) =>
  `bolao2026:recent-scores:${poolSlug}`;
const MAX_RECENTS = 3;

interface RecentScore {
  home: number;
  away: number;
}

function readRecents(poolSlug: string): RecentScore[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY(poolSlug));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s): s is RecentScore =>
        typeof s === 'object' && s !== null && typeof (s as RecentScore).home === 'number' && typeof (s as RecentScore).away === 'number'
    );
  } catch {
    return [];
  }
}

function writeRecents(poolSlug: string, recents: RecentScore[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      RECENTS_STORAGE_KEY(poolSlug),
      JSON.stringify(recents.slice(0, MAX_RECENTS))
    );
  } catch {
    /* storage may be disabled */
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function rememberScore(poolSlug: string, home: number, away: number): void {
  const current = readRecents(poolSlug);
  // Remove the same score if already present so it bubbles to the front.
  const dedup = current.filter((s) => !(s.home === home && s.away === away));
  writeRecents(poolSlug, [{ home, away }, ...dedup]);
}

export interface QuickFillChipsProps {
  poolSlug: string;
  onPick: (home: number, away: number) => void;
  disabled?: boolean;
}

export default function QuickFillChips({ poolSlug, onPick, disabled }: QuickFillChipsProps) {
  const [recents, setRecents] = useState<RecentScore[]>([]);

  useEffect(() => {
    setRecents(readRecents(poolSlug));
  }, [poolSlug]);

  const handlePick = useCallback(
    (home: number, away: number) => {
      onPick(home, away);
    },
    [onPick]
  );

  // Build the chip list: recents first (deduped against COMMON_SCORES), then commons.
  const chips: QuickFillScore[] = [];
  for (const r of recents) {
    if (!COMMON_SCORES.some((c) => c.home === r.home && c.away === r.away)) {
      chips.push({ home: r.home, away: r.away, label: `${r.home}-${r.away}` });
    }
  }
  chips.push(...COMMON_SCORES);

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Placares rápidos">
      {chips.map((chip) => (
        <button
          key={`${chip.home}-${chip.away}`}
          type="button"
          onClick={() => handlePick(chip.home, chip.away)}
          disabled={disabled}
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
            'border border-neutral-200 dark:border-neutral-700',
            'bg-neutral-50 dark:bg-neutral-800/50 text-muted-foreground',
            'hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300',
            'hover:bg-primary-50 dark:hover:bg-primary-500/10',
            'transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-neutral-200 disabled:hover:bg-neutral-50 disabled:hover:text-muted-foreground'
          )}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}
